/**
 * Shared tax display and calculation utilities.
 * Used by browser modal views (MemberDetails, createSOW2, EditSOW, FounderAssesmentDetails, PreSowDashboard)
 * and PDF components (SowPdf).
 */

import * as T from "../utilities/frontendTypes";
import { getRegionCapabilities } from "./regionCapabilities";
import { formatCurrency } from "./formatCurrency";

export const formatTaxDisplay = (expense: { amount: string; tax: string; gst?: string; pst?: string }): string => {
  const amount = parseFloat(expense.amount);
  const taxRate = parseFloat(expense.tax);
  const total = formatCurrency(amount * taxRate + amount);
  if (expense.gst !== undefined && expense.pst !== undefined) {
    const gstRate = parseFloat(expense.gst);
    const pstRate = parseFloat(expense.pst);
    if (gstRate === 0 && pstRate === 0) return "No Tax";
    const parts: string[] = [];
    if (gstRate > 0) parts.push(`GST ${(gstRate * 100).toFixed(0)}%`);
    if (pstRate > 0) parts.push(`PST ${(pstRate * 100).toFixed(0)}%`);
    return `${parts.join(' + ')} = $ ${total}`;
  }
  if (taxRate === 0) return "No Tax";
  return `HST ${(taxRate * 100).toFixed(0)}% = $ ${total}`;
};

export const computeTaxBreakdown = (expenseList: { amount: string; gst?: string; pst?: string }[]) => {
  let gstTotal = 0;
  let pstTotal = 0;
  let gstRate = '0';
  let pstRate = '0';
  expenseList.forEach(e => {
    const amt = parseFloat(e.amount) || 0;
    const g = parseFloat(e.gst || '0') || 0;
    const p = parseFloat(e.pst || '0') || 0;
    gstTotal += amt * g;
    pstTotal += amt * p;
    if (g > 0 && gstRate === '0') gstRate = Math.round(g * 100).toString();
    if (p > 0 && pstRate === '0') pstRate = Math.round(p * 100).toString();
  });
  return { gstTotal, pstTotal, gstRate, pstRate };
};

// ── Retainer tax breakdown (used by retainer invoice views) ──

export type RetainerTaxBreakdown = {
  isSplitTax: boolean;
  // HST (single-tax regions: CT/IO)
  hstRate: string;
  sowHstTaxable: number;
  sowNonTaxable: number;
  sowHstAmount: number;
  sowAfterTax: number;
  retainerHstTaxable: number;
  retainerNonTaxable: number;
  retainerHstAmount: number;
  retainerAfterTax: number;
  // GST/PST (split-tax regions: NF)
  gstRate: string;
  pstRate: string;
  sowGstTaxable: number;
  sowPstTaxable: number;
  sowGstAmount: number;
  sowPstAmount: number;
  retainerGstTaxable: number;
  retainerPstTaxable: number;
  retainerGstAmount: number;
  retainerPstAmount: number;
};

export function computeRetainerTaxBreakdown(sowItems: T.SowItem[], region: string | null | undefined): RetainerTaxBreakdown {
  const capabilities = getRegionCapabilities(region);
  const isSplitTax = capabilities.taxConfig.type === 'split';
  const allExpenses = (sowItems || []).flatMap(item => item.costBreakdown || []);

  let hstRate = '0';
  let gstRate = '0';
  let pstRate = '0';
  let sowHstTaxable = 0, sowNonTaxable = 0, sowHstAmount = 0;
  let sowGstTaxable = 0, sowPstTaxable = 0, sowGstAmount = 0, sowPstAmount = 0;

  if (isSplitTax) {
    allExpenses.forEach(e => {
      const amount = parseFloat(e.amount) || 0;
      const g = parseFloat(e.gst || '0') || 0;
      const p = parseFloat(e.pst || '0') || 0;

      if (g > 0) {
        sowGstTaxable += amount;
        sowGstAmount += amount * g;
        if (gstRate === '0') gstRate = Math.round(g * 100).toString();
      }
      if (p > 0) {
        sowPstTaxable += amount;
        sowPstAmount += amount * p;
        if (pstRate === '0') pstRate = Math.round(p * 100).toString();
      }
      if (g === 0 && p === 0) {
        sowNonTaxable += amount;
      }
    });
  } else {
    allExpenses.forEach(e => {
      const amount = parseFloat(e.amount) || 0;
      const taxRateParsed = parseFloat(e.tax);
      const taxRate = isNaN(taxRateParsed) ? 0 : taxRateParsed;

      if (taxRate > 0) {
        sowHstTaxable += amount;
        sowHstAmount += amount * taxRate;
        if (hstRate === '0') hstRate = Math.round(taxRate * 100).toString();
      } else {
        sowNonTaxable += amount;
      }
    });
  }

  const totalPreTax = allExpenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
  const sowAfterTax = isSplitTax
    ? totalPreTax + sowGstAmount + sowPstAmount
    : totalPreTax + sowHstAmount;

  return {
    isSplitTax,
    hstRate, gstRate, pstRate,
    sowHstTaxable, sowNonTaxable, sowHstAmount, sowAfterTax,
    sowGstTaxable, sowPstTaxable, sowGstAmount, sowPstAmount,
    retainerHstTaxable: sowHstTaxable * 0.5,
    retainerNonTaxable: sowNonTaxable * 0.5,
    retainerHstAmount: sowHstAmount * 0.5,
    retainerAfterTax: sowAfterTax * 0.5,
    retainerGstTaxable: sowGstTaxable * 0.5,
    retainerPstTaxable: sowPstTaxable * 0.5,
    retainerGstAmount: sowGstAmount * 0.5,
    retainerPstAmount: sowPstAmount * 0.5,
  };
}

// ── Tax rate label (used by Summary of Charges header) ──

export function getTaxRateLabel(sowItems: T.SowItem[]): string {
  const allExpenses = (sowItems || []).flatMap(item => item.costBreakdown || []);
  let hstRate = 0, gstRate = 0, pstRate = 0;

  allExpenses.forEach(e => {
    const g = parseFloat(e.gst || "0") || 0;
    const p = parseFloat(e.pst || "0") || 0;
    // Check both SoW tax rate and invoice tax rate
    const taxRateParsed = parseFloat(e.tax);
    const t = isNaN(taxRateParsed) ? 0 : taxRateParsed;
    const invoiceRate = parseFloat(e.invoiceTaxRate || "0") || 0;
    const effectiveRate = t > 0 ? t : invoiceRate;

    if (g > 0 && gstRate === 0) gstRate = Math.round(g * 100);
    if (p > 0 && pstRate === 0) pstRate = Math.round(p * 100);
    if (effectiveRate > 0 && hstRate === 0 && g === 0) hstRate = Math.round(effectiveRate * 100);
  });

  if (gstRate > 0 || pstRate > 0) {
    const parts: string[] = [];
    if (gstRate > 0) parts.push(`GST ${gstRate}%`);
    if (pstRate > 0) parts.push(`PST ${pstRate}%`);
    return `Taxable (${parts.join(" + ")})`;
  }
  if (hstRate > 0) return `Taxable at ${hstRate}%`;
  return "Taxable";
}

// ── Per-expense invoice tax label (tax type + rate) ──
//
// Returns the tax type and rate for a single cost item as displayed on an
// invoice line, e.g. "GST 5% + PST 7%" (split-tax regions like NorthForge),
// "HST 13%" (single-tax regions), or "No Tax". The label always prefers the
// invoice-time rate the SP set over the signed SoW rate (R21): split-tax reads
// invoiceGst/invoicePst (falling back to signed gst/pst), single-tax reads
// invoiceTaxRate (falling back to the signed tax), so the label matches the
// recomputed invoiceTax on the cost-of-work row.
export function getExpenseInvoiceTaxLabel(expense: {
  tax?: string;
  gst?: string;
  pst?: string;
  invoiceTaxRate?: string;
  invoiceGst?: string;
  invoicePst?: string;
}): string {
  const gst = parseFloat(expense.invoiceGst ?? expense.gst ?? "0") || 0;
  const pst = parseFloat(expense.invoicePst ?? expense.pst ?? "0") || 0;

  if (gst > 0 || pst > 0) {
    const parts: string[] = [];
    if (gst > 0) parts.push(`GST ${Math.round(gst * 100)}%`);
    if (pst > 0) parts.push(`PST ${Math.round(pst * 100)}%`);
    return parts.join(" + ");
  }

  const invoiceRate = parseFloat(expense.invoiceTaxRate || "0") || 0;
  const sowRate = parseFloat(expense.tax || "0") || 0;
  const effectiveRate = invoiceRate > 0 ? invoiceRate : sowRate;
  if (effectiveRate > 0) return `HST ${Math.round(effectiveRate * 100)}%`;
  return "No Tax";
}

// ── Summary of Charges (used by Final Invoice PDF + admin on-screen view) ──

export type ChargeRow = {
  label: string;
  taxable: number;
  nonTaxable: number;
  tax: number;
  total: number;
};

export type SummaryOfCharges = {
  sow: ChargeRow;
  costOfWork: ChargeRow;
  coInvestment: ChargeRow | null;
  retainer: ChargeRow | null;
  finalInvoice: ChargeRow;
};

export function computeSummaryOfCharges(
  sowItems: T.SowItem[],
  coPayPercentage: number,
  isTier2: boolean,
  associatedRetainerInvoiceTotal: number
): SummaryOfCharges {
  const allExpenses = (sowItems || []).flatMap(item => item.costBreakdown || []);

  // SoW — from signed SOW amounts
  let sowTaxable = 0, sowNonTaxable = 0, sowTax = 0;
  allExpenses.forEach(e => {
    const amount = parseFloat(e.amount) || 0;
    const taxRateParsed = parseFloat(e.tax);
    const taxRate = isNaN(taxRateParsed) ? 0 : taxRateParsed;

    if (taxRate === 0) {
      sowNonTaxable += amount;
    } else {
      sowTaxable += amount;
      sowTax += amount * taxRate;
    }
  });

  // Cost of Total Work Performed — from actual invoice amounts. Bucket by the
  // invoice-time rate the SP set (R21), falling back to the signed rate when the
  // line was never edited; the SoW row above always uses the signed rate.
  let workTaxable = 0, workNonTaxable = 0, workTax = 0;
  allExpenses.forEach(e => {
    const invAmount = Number(e.invoiceAmount) || 0;
    const invTax = Number(e.invoiceTax) || 0;
    const taxRateParsed = parseFloat(e.invoiceTaxRate ?? e.tax);
    const taxRate = isNaN(taxRateParsed) ? 0 : taxRateParsed;

    if (taxRate === 0) {
      workNonTaxable += invAmount;
    } else {
      workTaxable += invAmount;
      workTax += invTax;
    }
  });

  // Co-investment (Tier 3 only)
  let coInvestment: ChargeRow | null = null;
  if (!isTier2 && coPayPercentage > 0) {
    const rate = coPayPercentage / 100;
    const taxable = workTaxable * rate;
    const nonTaxable = workNonTaxable * rate;
    const tax = workTax * rate;
    coInvestment = {
      label: `Co-investment (${coPayPercentage}%)`,
      taxable, nonTaxable, tax,
      total: taxable + nonTaxable + tax
    };
  }

  // Retainer Invoice (if applicable — always 50% of SoW)
  let retainer: ChargeRow | null = null;
  if (associatedRetainerInvoiceTotal > 0) {
    const taxable = sowTaxable * 0.5;
    const nonTaxable = sowNonTaxable * 0.5;
    const tax = sowTax * 0.5;
    retainer = {
      label: "Retainer Invoice",
      taxable, nonTaxable, tax,
      total: taxable + nonTaxable + tax
    };
  }

  // Final Invoice = Cost of Work - Co-investment - Retainer
  const finalTaxable = workTaxable - (coInvestment?.taxable ?? 0) - (retainer?.taxable ?? 0);
  const finalNonTaxable = workNonTaxable - (coInvestment?.nonTaxable ?? 0) - (retainer?.nonTaxable ?? 0);
  const finalTax = workTax - (coInvestment?.tax ?? 0) - (retainer?.tax ?? 0);

  return {
    sow: {
      label: "Statement of Work",
      taxable: sowTaxable, nonTaxable: sowNonTaxable, tax: sowTax,
      total: sowTaxable + sowNonTaxable + sowTax
    },
    costOfWork: {
      label: "Cost of Total Work Performed",
      taxable: workTaxable, nonTaxable: workNonTaxable, tax: workTax,
      total: workTaxable + workNonTaxable + workTax
    },
    coInvestment,
    retainer,
    finalInvoice: {
      label: "Final Invoice",
      taxable: finalTaxable, nonTaxable: finalNonTaxable, tax: finalTax,
      total: finalTaxable + finalNonTaxable + finalTax
    },
  };
}

// ── Split-tax Summary of Charges (NorthForge / GST + PST regions) ──
//
// Split-tax regions break the single "Taxable" column into three buckets keyed
// off each cost item's GST/PST rates, and report PST and GST tax separately:
//   - taxablePstGst: items with both PST and GST > 0  (PST XX% and GST 5%)
//   - taxablePstOnly: items with PST > 0, GST = 0      (PST XX% and GST 0%)
//   - taxableGstOnly: items with GST > 0, PST = 0      (GST 5% and PST 0%)
//   - nonTaxable:     items with both GST and PST = 0
// GST in NF is always 0% or 5%; PST is 6/7/8%.

export type SplitChargeRow = {
  label: string;
  taxablePstGst: number;
  taxablePstOnly: number;
  taxableGstOnly: number;
  nonTaxable: number;
  pstTax: number;
  gstTax: number;
  total: number;
};

export type SplitSummaryOfCharges = {
  sow: SplitChargeRow;
  costOfWork: SplitChargeRow;
  coInvestment: SplitChargeRow | null;
  retainer: SplitChargeRow | null;
  finalInvoice: SplitChargeRow;
  pstRate: string; // first non-zero PST rate as a whole-number string, e.g. "7"
  gstRate: string; // first non-zero GST rate as a whole-number string, e.g. "5"
};

// Bucket a list of {base amount, gst rate, pst rate} into a SplitChargeRow.
function bucketSplitRow(
  label: string,
  items: { base: number; gstRate: number; pstRate: number }[]
): SplitChargeRow {
  let taxablePstGst = 0, taxablePstOnly = 0, taxableGstOnly = 0, nonTaxable = 0;
  let pstTax = 0, gstTax = 0;

  items.forEach(({ base, gstRate, pstRate }) => {
    const hasGst = gstRate > 0;
    const hasPst = pstRate > 0;
    if (hasPst && hasGst) taxablePstGst += base;
    else if (hasPst) taxablePstOnly += base;
    else if (hasGst) taxableGstOnly += base;
    else nonTaxable += base;

    pstTax += base * pstRate;
    gstTax += base * gstRate;
  });

  const total = taxablePstGst + taxablePstOnly + taxableGstOnly + nonTaxable + pstTax + gstTax;
  return { label, taxablePstGst, taxablePstOnly, taxableGstOnly, nonTaxable, pstTax, gstTax, total };
}

// Scale every monetary field of a row by a rate (for Co-investment / Retainer).
function scaleSplitRow(label: string, row: SplitChargeRow, rate: number): SplitChargeRow {
  return {
    label,
    taxablePstGst: row.taxablePstGst * rate,
    taxablePstOnly: row.taxablePstOnly * rate,
    taxableGstOnly: row.taxableGstOnly * rate,
    nonTaxable: row.nonTaxable * rate,
    pstTax: row.pstTax * rate,
    gstTax: row.gstTax * rate,
    total: row.total * rate,
  };
}

// base - sum(subtractions), field by field.
function subtractSplitRows(label: string, base: SplitChargeRow, subs: (SplitChargeRow | null)[]): SplitChargeRow {
  const sub = (key: keyof Omit<SplitChargeRow, "label">) =>
    base[key] - subs.reduce((acc, s) => acc + (s ? s[key] : 0), 0);
  return {
    label,
    taxablePstGst: sub("taxablePstGst"),
    taxablePstOnly: sub("taxablePstOnly"),
    taxableGstOnly: sub("taxableGstOnly"),
    nonTaxable: sub("nonTaxable"),
    pstTax: sub("pstTax"),
    gstTax: sub("gstTax"),
    total: sub("total"),
  };
}

export function computeSplitSummaryOfCharges(
  sowItems: T.SowItem[],
  coPayPercentage: number,
  isTier2: boolean,
  associatedRetainerInvoiceTotal: number,
  updatedSowItems?: T.SowItem[] // Updated items with live UI changes (for Work row)
): SplitSummaryOfCharges {
  // SoW row uses signed SoW amounts; Cost-of-Work row uses actual invoice amounts.
  const sowExpenses = (sowItems || []).flatMap(item => item.costBreakdown || []);
  const workExpenses = (updatedSowItems || sowItems || []).flatMap(item => item.costBreakdown || []);

  const rateOf = (v?: string) => parseFloat(v || "0") || 0;

  const sow = bucketSplitRow("Statement of Work", sowExpenses.map(e => ({
    base: parseFloat(e.amount) || 0,
    gstRate: rateOf(e.gst),
    pstRate: rateOf(e.pst),
  })));

  // Cost-of-work row buckets by the invoice-time GST/PST the SP set (R21),
  // falling back to the signed split when a line was never edited.
  const costOfWork = bucketSplitRow("Cost of Total Work Performed", workExpenses.map(e => ({
    base: Number(e.invoiceAmount) || 0,
    gstRate: rateOf(e.invoiceGst ?? e.gst),
    pstRate: rateOf(e.invoicePst ?? e.pst),
  })));

  // Co-investment (Tier 3 only) — a percentage of the work performed.
  let coInvestment: SplitChargeRow | null = null;
  if (!isTier2 && coPayPercentage > 0) {
    coInvestment = scaleSplitRow(`Co-investment (${coPayPercentage}%)`, costOfWork, coPayPercentage / 100);
  }

  // Retainer Invoice (if applicable — always 50% of SoW).
  let retainer: SplitChargeRow | null = null;
  if (associatedRetainerInvoiceTotal > 0) {
    retainer = scaleSplitRow("Retainer Invoice", sow, 0.5);
  }

  const finalInvoice = subtractSplitRows("Final Invoice", costOfWork, [coInvestment, retainer]);

  // Header rates: first non-zero GST/PST rate across all expenses.
  let pstRate = "0", gstRate = "0";
  [...sowExpenses, ...workExpenses].forEach(e => {
    const g = Math.round(rateOf(e.gst) * 100);
    const p = Math.round(rateOf(e.pst) * 100);
    if (g > 0 && gstRate === "0") gstRate = String(g);
    if (p > 0 && pstRate === "0") pstRate = String(p);
  });

  return { sow, costOfWork, coInvestment, retainer, finalInvoice, pstRate, gstRate };
}
